import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  AIAction,
  AIResponse,
  ProviderInfo,
  AvailableModelsResponse,
} from "../types/ai";
import { api } from "../lib/api";

export interface AISuggestion {
  id: string;
  original: string;
  suggestion: string;
  action: AIAction;
  status: "pending" | "accepted" | "rejected";
}

interface AIState {
  // Provider / model selection (persisted)
  selectedProvider: string | null;
  selectedModel: string | null;
  completionProvider: string | null;
  completionModel: string | null;
  availableProviders: ProviderInfo[];
  defaultProvider: string;
  defaultModel: string;

  // Processing state
  isProcessing: boolean;
  currentSuggestion: AISuggestion | null;
  streamingText: string;
  _streamAbort: AbortController | null;

  // Explain popover
  errorExplanation: string | null;
  isExplaining: boolean;

  // Actions
  fetchModels: () => Promise<void>;
  setProvider: (providerId: string, modelId: string) => void;
  setCompletionProvider: (providerId: string, modelId: string) => void;
  clearCompletionProvider: () => void;

  editText: (text: string, action: AIAction, context: string, docType: string) => Promise<AIResponse>;
  editTextStream: (text: string, action: AIAction, context: string, docType: string) => Promise<void>;
  explainText: (text: string, context?: string) => Promise<AIResponse>;
  convertText: (text: string, fromFormat: string, toFormat: string) => Promise<AIResponse>;
  explainError: (errorMessage: string, sourceContext?: string) => Promise<AIResponse>;

  cancelStream: () => void;
  acceptSuggestion: () => void;
  rejectSuggestion: () => void;
  clearSuggestion: () => void;
  clearExplanation: () => void;
}

let suggestionCounter = 0;

function modelPayload(state: AIState) {
  const provider = state.selectedProvider || undefined;
  const model = state.selectedModel || undefined;
  return { provider, model };
}

export const useAIStore = create<AIState>()(
  persist(
    (set, get) => ({
      // Defaults
      selectedProvider: null,
      selectedModel: null,
      completionProvider: null,
      completionModel: null,
      availableProviders: [],
      defaultProvider: "gemini",
      defaultModel: "gemini-2.5-flash",

      isProcessing: false,
      currentSuggestion: null,
      streamingText: "",
      _streamAbort: null,

      errorExplanation: null,
      isExplaining: false,

      // ------------------------------------------------------------------
      // Model management
      // ------------------------------------------------------------------

      fetchModels: async () => {
        try {
          const resp = await api.get<AvailableModelsResponse>("/api/v1/ai/models");
          set({
            availableProviders: resp.providers,
            defaultProvider: resp.default_provider,
            defaultModel: resp.default_model,
          });

          const { selectedProvider, selectedModel } = get();
          if (!selectedProvider || !selectedModel) {
            const firstProvider = resp.providers[0];
            if (firstProvider) {
              set({
                selectedProvider: selectedProvider || firstProvider.id,
                selectedModel: selectedModel || firstProvider.models[0]?.id || resp.default_model,
              });
            }
          }
        } catch {
          // Silently fall back to defaults if backend is unreachable
        }
      },

      setProvider: (providerId, modelId) => {
        set({ selectedProvider: providerId, selectedModel: modelId });
      },

      setCompletionProvider: (providerId, modelId) => {
        set({ completionProvider: providerId, completionModel: modelId });
      },

      clearCompletionProvider: () => {
        set({ completionProvider: null, completionModel: null });
      },

      // ------------------------------------------------------------------
      // Edit (non-streaming)
      // ------------------------------------------------------------------

      editText: async (text, action, context, docType) => {
        set({ isProcessing: true });
        try {
          const result = await api.post<AIResponse>("/api/v1/ai/edit", {
            text,
            action,
            context,
            doc_type: docType,
            ...modelPayload(get()),
          });
          set({
            currentSuggestion: {
              id: `suggestion-${++suggestionCounter}`,
              original: text,
              suggestion: result.result,
              action,
              status: "pending",
            },
          });
          return result;
        } finally {
          set({ isProcessing: false });
        }
      },

      // ------------------------------------------------------------------
      // Edit (streaming)
      // ------------------------------------------------------------------

      editTextStream: async (text, action, context, docType) => {
        get()._streamAbort?.abort();
        const controller = new AbortController();
        set({ isProcessing: true, streamingText: "", _streamAbort: controller });
        try {
          let accumulated = "";
          for await (const chunk of api.stream("/api/v1/ai/edit/stream", {
            text,
            action,
            context,
            doc_type: docType,
            ...modelPayload(get()),
          }, controller.signal)) {
            accumulated += chunk;
            set({ streamingText: accumulated });
          }
          set({
            currentSuggestion: {
              id: `suggestion-${++suggestionCounter}`,
              original: text,
              suggestion: accumulated,
              action,
              status: "pending",
            },
            streamingText: "",
            _streamAbort: null,
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          throw err;
        } finally {
          set({ isProcessing: false });
        }
      },

      // ------------------------------------------------------------------
      // Explain / convert / error-explain
      // ------------------------------------------------------------------

      explainText: async (text, context) => {
        set({ isProcessing: true });
        try {
          return await api.post<AIResponse>("/api/v1/ai/explain", {
            text,
            context,
            ...modelPayload(get()),
          });
        } finally {
          set({ isProcessing: false });
        }
      },

      convertText: async (text, fromFormat, toFormat) => {
        set({ isProcessing: true });
        try {
          return await api.post<AIResponse>("/api/v1/ai/convert", {
            text,
            from_format: fromFormat,
            to_format: toFormat,
            ...modelPayload(get()),
          });
        } finally {
          set({ isProcessing: false });
        }
      },

      explainError: async (errorMessage, sourceContext) => {
        set({ isExplaining: true, errorExplanation: null });
        try {
          const resp = await api.post<AIResponse>("/api/v1/ai/error-explain", {
            error_message: errorMessage,
            source_context: sourceContext,
            ...modelPayload(get()),
          });
          set({ errorExplanation: resp.result });
          return resp;
        } finally {
          set({ isExplaining: false });
        }
      },

      // ------------------------------------------------------------------
      // Suggestion lifecycle
      // ------------------------------------------------------------------

      cancelStream: () => {
        const { _streamAbort } = get();
        if (_streamAbort) {
          _streamAbort.abort();
          set({ _streamAbort: null, isProcessing: false, streamingText: "" });
        }
      },

      acceptSuggestion: () => {
        const { currentSuggestion } = get();
        if (currentSuggestion) {
          set({ currentSuggestion: { ...currentSuggestion, status: "accepted" } });
        }
      },

      rejectSuggestion: () => {
        const { currentSuggestion } = get();
        if (currentSuggestion) {
          set({ currentSuggestion: { ...currentSuggestion, status: "rejected" } });
        }
      },

      clearSuggestion: () => set({ currentSuggestion: null, streamingText: "" }),
      clearExplanation: () => set({ errorExplanation: null }),
    }),
    {
      name: "lever-ai",
      partialize: (state) => ({
        selectedProvider: state.selectedProvider,
        selectedModel: state.selectedModel,
        completionProvider: state.completionProvider,
        completionModel: state.completionModel,
      }),
    },
  ),
);
